import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Typography, Descriptions, Tag, Space, Empty, Statistic } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';

const { Title, Text, Paragraph } = Typography;
const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

interface CallLogDetailProps {
	callSid: string;
}

const formatMetricName = (name: string) => {
	return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const CallLogDetail: React.FC<CallLogDetailProps> = ({ callSid }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [callLog, setCallLog] = useState<any>(null);

	useEffect(() => {
		if (!callSid) {
			setError('No Call SID provided.');
			setLoading(false);
			return;
		}

		const fetchCallLogDetail = async () => {
			setLoading(true);
			setError(null);
			try {
				const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
				const response = await axios.get(`${baseUrl}/api/admin/call-logs/${callSid}`, {
					headers: { Authorization: `Bearer ${token}` },
				});
				setCallLog(response.data.data);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load call details.');
			} finally {
				setLoading(false);
			}
		};

		fetchCallLogDetail();
	}, [callSid]);

	if (loading) {
		return <Spin tip="Loading Call Details..." />;
	}

	if (error) {
		return <Alert message="Error" description={error} type="error" showIcon />;
	}

	if (!callLog) {
		return <Empty description="Call log details not found." />;
	}

	const callScore = callLog.callScore ? JSON.parse(callLog.callScore) : null;

	return (
		<Space direction="vertical" size="large" style={{ width: '100%' }}>
			<Card>
				<Title level={4}>Call Details: {callLog.callSid}</Title>
				<Descriptions bordered column={2}>
					<Descriptions.Item label="Date">
						{dayjs(callLog.date).format('YYYY-MM-DD HH:mm:ss')}
					</Descriptions.Item>
					<Descriptions.Item label="Duration">{callLog.duration} seconds</Descriptions.Item>
					<Descriptions.Item label="From">{callLog.from}</Descriptions.Item>
					<Descriptions.Item label="To">{callLog.to}</Descriptions.Item>
					<Descriptions.Item label="Source">{callLog.forwardedFrom}</Descriptions.Item>
				</Descriptions>
			</Card>

			{callScore && (
				<Card title="Overall Score & Summary">
					<Statistic title="Overall Call Score" value={callScore.value} suffix="/ 5" />
					<Paragraph style={{ marginTop: 16 }}>
						<Text strong>Summary: </Text>
						{callLog.callSummary}
					</Paragraph>
					<Paragraph>
						<Text strong>Score Description: </Text>
						{callScore.description}
					</Paragraph>
				</Card>
			)}

			<Card title="Evaluation Metrics">
				<Descriptions bordered column={2} size="small">
					{Object.keys(callLog)
						.filter((key) => key.endsWith('_impact') || key.endsWith('_handling'))
						.map((key) => (
							<React.Fragment key={key}>
								<Descriptions.Item label={formatMetricName(key)}>
									<Tag
										color={
											callLog[key].score > 7
												? 'success'
												: callLog[key].score > 4
													? 'warning'
													: 'error'
										}
									>
										{callLog[key].score} / 10
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Comment">{callLog[key].comment}</Descriptions.Item>
							</React.Fragment>
						))}
				</Descriptions>
			</Card>
		</Space>
	);
};

export default CallLogDetail;
